import Button from '@base/Button/Button';
import H1 from '@base/H1/H1';
import H2 from '@base/H2/H2';
import Image from '@base/Image';
import Tags from '@base/Tags/Tags';
import BatchShare from '@cont/Batch/BatchShare';
import BatchThumnail from '@cont/Batch/BatchThumbNail';
import styled from '@emotion/styled';
import CalendarSleek from '@icon/CalendarSleek';
import EducatorAvatar from '@icon/EducatorAvatar';
import { getLink, getLinkPath } from '@models/Batch';
import { Spacings, Fonts, breakpoints, mediaQuery } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { getImage } from '@utils/imageHelper';
import {
  isBefore,
  isFuture,
  isEqual,
  differenceInDays,
  differenceInSeconds,
  format
} from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import segmentStorage from '@events/segmentStorage';
import { useRouter } from 'next/router';
import Notes from '@icon/NotesV2';
import useWindowSize from '@hooks/useWindowSize';
import { emptyList } from '@constants/empty';
import PlayRectangle from '@icon/PlayRectangleV2';
import { getBatchTag } from '@comp/BatchCard/util';

const BatchContainer = styled.div`
  width: 1137px;
  ${(props) =>
    props.imgUrl && `background-image: url('${getImage(props.imgUrl)}');`}
  background-repeat: no-repeat;
  background-position: right top;
  background-origin: content-box;
  margin-inline: auto;
  margin-top: ${Spacings.SPACING_40B};
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_20B};
    width: 728px;
    padding: ${Spacings.SPACING_4B};
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: ${Spacings.SPACING_4B};
    margin-top: 62px;
    background-image: none;
  }
`;

const Header = styled(H1)`
  padding-right: ${Spacings.SPACING_24B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Spacings.SPACING_5B};
    line-height: ${Spacings.SPACING_6B};
    padding: 0px;
  }
`;

const CardHeader = styled(H2)`
  color: var(--color-text-primary);
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;

const BatchCard = styled.div`
  padding: ${Spacings.SPACING_8B};
  background-color: var(--color-i-white);
  border-radius: ${Spacings.SPACING_2B};
  display: flex;
  margin-top: ${Spacings.SPACING_16B};
  box-shadow: ${boxShadowGenerator()};
  ${mediaQuery.uptoTablet} {
    flex-direction: column;
    padding: ${Spacings.SPACING_4B};
    margin-top: ${Spacings.SPACING_4B};
    gap: ${Spacings.SPACING_4B};
  }
  .course-thumbnail,
  .intro-video {
    min-width: 560px;
    height: 316px;
    ${mediaQuery.uptoMobile} {
      min-width: auto;
      width: 100%;
      height: 167px;
    }
  }
  .course-thumbnail {
    [class*='PreviewButton'] {
      left: ${Spacings.SPACING_4B};
      bottom: ${Spacings.SPACING_4B};
      ${mediaQuery.uptoMobile} {
        left: ${Spacings.SPACING_3B};
        bottom: ${Spacings.SPACING_3B};
      }
    }
  }
`;

const Points = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_3B};
  margin-top: ${Spacings.SPACING_5B};
`;

const Point = styled.div`
  margin-right: ${Spacings.SPACING_4B};
  display: flex;
  align-items: center;
  gap: 9px;
`;

const Text = styled.div`
  font-size: ${Fonts.H5};
  font-weight: ${Fonts.LIGHT};
  line-height: ${Spacings.SPACING_5B};
  color: var(--color-text-primary);
  margin-left: ${Spacings.SPACING_1B};
`;

const TimerText = styled.div`
  font-size: ${Fonts.H6};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: ${Spacings.SPACING_18};
  color: ${({ alert }) => (alert ? '#EB5757' : 'var(--color-text-secondary)')};
  margin-top: ${Spacings.SPACING_4B};
  display: flex;
  gap: ${Spacings.SPACING_1B};
  ::before {
    content: ' ';
    ${({ imgUrl }) =>
      imgUrl
        ? `background-image:url(${getImage(
            imgUrl
          )}); background-position: center;`
        : ''}
    ${({ alert }) => (!alert ? 'display:contents;' : '')}
    width: 18px;
    aspect-ratio: 1;
  }
`;

const CardInfo = styled.div`
  width: 100%;
  position: relative;
  .point {
    margin-right: ${Spacings.SPACING_4B};
    display: flex;
    align-items: center;
    gap: ${Spacings.SPACING_2B};
    margin-bottom: ${Spacings.SPACING_4B};
    :last-child {
      margin-bottom: 0;
    }
    svg {
      align-self: flex-start;
    }
  }
  .text {
    font-size: ${Fonts.H6};
    font-weight: ${Fonts.LIGHT};
    line-height: ${Spacings.SPACING_5B};
    color: var(--color-text-primary);
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${Spacings.SPACING_4B};
  width: 100%;
  margin-top: ${Spacings.SPACING_4B};
  button {
    width: calc(50% - ${Spacings.SPACING_2B});
    margin: 0;
    ${mediaQuery.uptoMobile} {
      &:first-child {
        width: 60%;
      }
      width: 40%;
    }
  }
  ${mediaQuery.uptoTablet} {
    position: relative;
  }
`;

const StyledButton = styled(Button)`
  margin-top: ${Spacings.SPACING_3B};
  width: 100%;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin: 0 ${Spacings.SPACING_2B} 0 0;
  }
`;

const TagWrapper = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;

const Tag1 = Tags.withComponent('div');
const Tag = styled(Tag1)`
  background-color: transparent;
  display: inline-flex;
  gap: ${Spacings.SPACING_1B};
  max-height: ${Spacings.SPACING_6B};
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  ::after {
    ${({ imgUrl }) => (imgUrl ? `content: url('${imgUrl}');` : '')}
    display: ${(props) => (props.imgUrl ? 'block' : 'contents')};
    align-self: center;
  }
`;

const HeroBatchSection = (props) => {
  const {
    block: {
      data: [batch]
    },
    goalDetails: { name: goalName }
  } = props;
  const { isMobile } = useWindowSize();
  const bgUrl = 'goal/heroBatch.svg';
  const batchLive = 'batch-live.svg';
  const clockTimer = 'clock-timer-fast.svg';
  const [formattedTime, setTime] = useState(null);
  const [classStatus, setClassStatus] = useState(null);
  const timer = useRef(null);
  const alert = useRef(false);
  const { push } = useRouter();

  const start = useRef(new Date(batch.startsAt));
  const end = useRef(new Date(batch.completedAt));

  const getAuthors = () => {
    const authorsNameArr =
      batch.authors?.map(
        (author) => `${author.firstName} ${author.lastName}`
      ) || emptyList;
    if (authorsNameArr.length) {
      if (authorsNameArr.length < 2) {
        return authorsNameArr[0];
      }
      if (authorsNameArr.length > 1 && authorsNameArr.length < 4) {
        return `${authorsNameArr
          .slice(0, -1)
          .join(', ')} and ${authorsNameArr.slice(-1)}`;
      }
      return `${authorsNameArr.slice(0, 3).join(', ')} and ${
        authorsNameArr.length - 3
      } more`;
    }
  };

  const getNotes = () => {
    const notesCount = batch?.notesCount || 0;
    let noteText = 'Topic-wise full syllabus notes';
    if (notesCount >= 50) {
      noteText = `${notesCount}+ topic-wise full syllabus notes `;
    }
    return noteText;
  };

  const getClasses = (type) => {
    const { lessonsCount = 0, quizzesCount, testSeriesCount } = batch;
    const totalCount = quizzesCount + testSeriesCount;
    if (type === 'live')
      return lessonsCount >= 20
        ? `${lessonsCount}+ live classes`
        : 'Interactive live classes';
    if (type === 'test')
      return totalCount >= 10 ? `${totalCount}+ test series` : 'test series';
  };

  const getFormattedTime = (time) => {
    let time1 = time;
    const secs = time1 % 60;
    time1 = Math.floor(time1 / 60);
    const mins = time1 % 60;
    const hrs = Math.floor(time1 / 60);
    time1 = `${hrs < 10 ? 0 : ''}${hrs}:${mins < 10 ? 0 : ''}${mins}:${
      secs < 10 ? 0 : ''
    }${secs}`;
    return time1;
  };

  const updateClassStatus = () => {
    const isLive =
      (isBefore(start.current, new Date()) ||
        isEqual(start.current, new Date())) &&
      isFuture(end.current);
    const isUpComing = isFuture(start.current);
    if (isLive) setClassStatus('live');
    else if (isUpComing) setClassStatus('upcoming');
    else setClassStatus('ended');
  };

  useEffect(() => {
    updateClassStatus();
  }, []);

  useEffect(() => {
    if (classStatus === 'upcoming') {
      let remainingTime = differenceInDays(start.current, new Date());
      if (remainingTime >= 1) {
        remainingTime += `${remainingTime === 1 ? ' day' : ' days'}`;
        setTime(remainingTime);
      } else {
        alert.current = true;
        timer.current = setInterval(() => {
          remainingTime = differenceInSeconds(start.current, new Date());
          if (remainingTime < 0) {
            clearInterval(timer.current);
            timer.current = null;
            updateClassStatus();
          }
          remainingTime = getFormattedTime(remainingTime);
          setTime(() => remainingTime);
        }, 1000);
      }
    } else if (classStatus === 'ended') {
      setTime(format(end.current, 'd MMM yyyy'));
    }

    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [classStatus]);

  const onPreview = () => {
    segmentStorage.setItem('lpss', 'Hero Batch Section');
  };

  const onViewSchedule = () => {
    segmentStorage.setItem('lpss', 'Hero Batch Section');
    push(getLinkPath(), getLink(batch));
  };

  return (
    <BatchContainer imgUrl={bgUrl}>
      <Header>
        Crack {goalName} {!isMobile && <br />} with {isMobile && <br />} our
        Achievers batch
      </Header>
      <Points>
        <Point>
          <Image src="/dst/tick.svg" height={20} width={20} alt="dst-points" />
          <Text>Exceptional educators teach this batch </Text>
        </Point>
        <Point>
          <Image src="/dst/tick.svg" height={20} width={20} alt="dst-points" />
          <Text>Learn along side our top learners</Text>
        </Point>
      </Points>
      <BatchCard>
        <BatchThumnail
          thumbnailHeight={316}
          thumbnailWidth={560}
          thumbnailPriority={false}
          batch={batch}
          batchTag={false}
          onPreview={onPreview}
        />
        <CardInfo>
          <TagWrapper>
            <Tag
              key={batch.languages[0].code}
              type="hollow"
              imgUrl={getImage('goal/2Star.svg')}
            >
              Achiever&apos;s batch
            </Tag>
            <Tag key={batch.languages[0].code} type="hollow">
              {batch.languages[0].label}
            </Tag>
          </TagWrapper>
          <CardHeader>{batch.name}</CardHeader>
          <div className="point">
            <CalendarSleek size="20px" />
            <span className="text">{getBatchTag(batch)}</span>
          </div>
          <div className="point">
            <PlayRectangle size="20px" />
            <span className="text">{getClasses('live')}</span>
            <span>&#183;</span>
            <span className="text">{getClasses('test')}</span>
          </div>
          <div className="point">
            <Notes size="20px" />
            <span className="text">{getNotes()}</span>
          </div>
          <div className="point">
            <EducatorAvatar size="20px" />
            <span className="text">{getAuthors()}</span>
          </div>
          <ButtonsWrapper>
            <StyledButton
              variant="filled"
              color="white"
              label="View full schedule"
              onClick={onViewSchedule}
            />
            <BatchShare batch={batch} />
          </ButtonsWrapper>
          {classStatus === 'live' && (
            <TimerText alert imgUrl={batchLive}>
              Batch is live now
            </TimerText>
          )}
          {classStatus === 'upcoming' && (
            <TimerText alert={alert.current} imgUrl={clockTimer}>
              Batch starts in {formattedTime}
            </TimerText>
          )}
          {classStatus === 'ended' && (
            <TimerText>Batch ended on {formattedTime}</TimerText>
          )}
        </CardInfo>
      </BatchCard>
    </BatchContainer>
  );
};

export default HeroBatchSection;
